import { FC, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LoadingSpinner } from '@medtronic/pecc-react-component-library-js';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { DatasetFile } from '~/api/types';
import {
  useDeleteDatasetFileMutation,
  useDownloadDatasetFileMutation,
  useGetDatasetQuery,
  useRenameDatasetFileMutation,
} from '~/api/v2/hooks';
import { DeleteTypeEnum, ModalTypeEnum, SelectedFilesTable } from '~/components';
import { RenameFilePayload } from '~/components/FileSystemActionModal';
import { VerticalDotsIcon } from '~/icons';
import { useAppDispatch } from '~/redux';
import { ModalTypesEnum, openModal } from '~/redux/slices/modalSlice';
import validateFileAndDownload from '~/utils/validateFileAndDownload';
import { moreButtonStyles } from '../styles';
import { ProgressStatus } from '../types';
import DatasetUploadingContext from './DatasetFilesUploadingContext';

const DatasetFilesActionCell: FC<
  GridRenderCellParams<
    DatasetFile & {
      id: string;
    }
  >
> = ({ row }) => {
  const dispatch = useAppDispatch();
  const datasetId = +useParams().id!;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const uploadingFiles = useContext(DatasetUploadingContext);
  const { data: datasetData } = useGetDatasetQuery({ datasetId });
  const [renameDatasetFile] = useRenameDatasetFileMutation();
  const [deleteDatasetFile] = useDeleteDatasetFileMutation();
  const [downloadDatasetFile] = useDownloadDatasetFileMutation();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const handleDownload = async (filePath: string) => {
    setIsDownloading(true);
    const result = await downloadDatasetFile({ datasetId, filePath }).unwrap();
    await validateFileAndDownload(result.url, {
      filePath,
      fileName: filePath.split('/').pop() || 'download',
    });
    setIsDownloading(false);
  };

  const uploadingFile = uploadingFiles?.uploadingSupportiveDocs.get(row.id);

  return (
    <>
      {isDownloading ? (
        <Box ml={3}>
          <LoadingSpinner spinnerSize="xs" />
        </Box>
      ) : (
        <Button
          disabled={uploadingFile?.status === ProgressStatus.RUNNING}
          sx={moreButtonStyles}
          endIcon={<VerticalDotsIcon style={{ marginLeft: '1rem' }} />}
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
        >
          More
        </Button>
      )}
      <Menu
        elevation={1}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        <MenuItem
          sx={{ color: 'electricBlue.main' }}
          onClick={() => {
            dispatch(
              openModal({
                type: ModalTypesEnum.DELETE_CONFIRMATION,
                payload: {
                  type: DeleteTypeEnum.FILES,
                  onDelete: () =>
                    deleteDatasetFile({
                      datasetId,
                      filePath: row.path,
                    }),
                  content: <SelectedFilesTable selectedFiles={[{ name: row.name, size: row.size }]} />,
                },
              }),
            );
            setAnchorEl(null);
          }}
          disabled={!datasetData?.supportive_docs_edit_allowed}
        >
          Delete
        </MenuItem>
        <MenuItem
          sx={{ color: 'electricBlue.main' }}
          onClick={() => {
            dispatch(
              openModal({
                type: ModalTypesEnum.FILE_SYSTEM_ACTION,
                payload: {
                  type: ModalTypeEnum.RENAME_FILE,
                  onRename: ({ current_path, new_path }: Parameters<RenameFilePayload['onRename']>[0]) =>
                    renameDatasetFile({
                      datasetId,
                      oldFilePath: current_path,
                      newFilePath: new_path,
                    }),
                  filePath: row.path || '',
                },
              }),
            );
            setAnchorEl(null);
          }}
          disabled={!datasetData?.supportive_docs_edit_allowed}
        >
          Rename
        </MenuItem>
        <MenuItem
          sx={{ color: 'electricBlue.main' }}
          onClick={() => {
            handleDownload(row.path);
            setAnchorEl(null);
          }}
        >
          Download
        </MenuItem>
      </Menu>
    </>
  );
};

export default DatasetFilesActionCell;
